// services.ts
import axios from 'axios';

import { Demandes } from './models/Demandes';
import { Voyageurs } from './models/Voyageurs';
import { PieceJointe } from './models/PieceJointe';
import { BASE_URL } from './api/config';



export const recupereStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/tableaudebord`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des stats :', error);
    throw error;
  }
};

// ---------- Alertes planifiées sur un dossier ----------
export interface AlerteDossier {
  id: number;
  id_demande: number;
  datetime_envoi: string;
  message: string;
  statut: 'en_attente' | 'envoye' | 'echec';
  /** null = alerte manuelle ; non null = marqueur pose par un cron (ex. 'relance_ecard_j4') */
  type?: string | null;
  date_creation?: string;
  date_envoi?: string | null;
}

export const recupererAlertes = async (demandeId: number): Promise<AlerteDossier[]> => {
  try {
    const response = await axios.get<AlerteDossier[]>(`${BASE_URL}/demandes/${demandeId}/alertes`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des alertes :', error);
    return [];
  }
};

export const creerAlerte = async (
  demandeId: number,
  datetimeEnvoi: string,
  message: string
): Promise<AlerteDossier> => {
  const response = await axios.post<AlerteDossier>(
    `${BASE_URL}/demandes/${demandeId}/alertes`,
    { datetime_envoi: datetimeEnvoi, message }
  );
  return response.data;
};

export const supprimerAlerte = async (alerteId: number): Promise<void> => {
  await axios.delete(`${BASE_URL}/alertes/${alerteId}`);
};

// ---------- Création manuelle d'un dossier (admin) ----------
export const recupererPaysListe = async (): Promise<string[]> => {
  try {
    const response = await axios.get<{ libelle: string }[]>(`${BASE_URL}/pays`);
    return response.data.map((p) => p.libelle).filter(Boolean);
  } catch (error) {
    console.error('Erreur lors de la récupération des pays :', error);
    return [];
  }
};

export const creerDossierManuel = async (data: {
  email: string;
  pays: string;
  validite: string;
  dateDemande: string;
  nom: string;
  prenom: string;
  dateDepart: string;
  etatVisa: string;
}): Promise<{ id: number; chemin: string; id_voyageur: number }> => {
  const response = await axios.post(`${BASE_URL}/demandes`, data);
  return response.data;
};


// ---------- Adresse de facturation d'un dossier créé manuellement (admin) ----------
// Mêmes champs que « Information de facturation » côté client ; adresse_ligne2 facultative
export interface AdresseFacturationSaisie {
  nom: string;
  adresse_ligne1: string;
  adresse_ligne2: string;
  ville: string;
  code_postal: string;
  pays: string;
  telephone: string;
}

// Adresse déjà enregistrée sur le compte du client (ou son groupe)
export type AdresseFacturationCompte = AdresseFacturationSaisie & { id: number };

// Bon de commande du dossier (null tant que l'adresse n'a pas été saisie)
export type BdcDossierManuel = AdresseFacturationSaisie & { id: number; etat: string; total: number | null };

// 403 si le dossier n'a pas été créé manuellement
export const recupererFacturationDossierManuel = async (
  demandeId: number
): Promise<{ adresses: AdresseFacturationCompte[]; bdc: BdcDossierManuel | null }> => {
  const response = await axios.get(`${BASE_URL}/demandes/${demandeId}/facturation-manuelle`);
  return response.data;
};

// idAdresse : adresse du compte reprise (mise à jour), 0 = nouvelle adresse. Crée le BDC à 0 € s'il n'existe pas ;
// 409 si le BDC est déjà payé
export const enregistrerAdresseFacturationDossierManuel = async (
  demandeId: number,
  adresse: AdresseFacturationSaisie,
  idAdresse: number
): Promise<{ bonId: number; adresseId: number; bdcCree: boolean }> => {
  const response = await axios.put(`${BASE_URL}/demandes/${demandeId}/adresse-facturation`, { ...adresse, id_adresse: idAdresse });
  return response.data;
};

// Nouvelle fonction pour traiter l'assurance
export const traitementAssurance = async (demandeId: number, newAssuranceValue: string) => {
  try {
    // On envoie la nouvelle valeur dans le body de la requête PUT
    const response = await axios.put(
      `${BASE_URL}/demandes/${demandeId}/traitement-assurance`,
      { newAssuranceValue }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du traitement de l'assurance :", error);
    throw error;
  }
};

// Fonction pour mettre à jour directement l'état visa d'une demande (édition inline dans la liste)
export const majEtatVisa = async (id: number, etatVisa: string) => {
  try {
    const response = await axios.put(`${BASE_URL}/demandes/${id}/etat`, { etatVisa });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'état visa:", error);
    throw error;
  }
};

// Mise à jour directe de l'état de paiement d'une demande (édition inline dans la liste) ; '' = aucun
export const majEtatPaiement = async (id: number, etatPaiement: string) => {
  try {
    const response = await axios.put(`${BASE_URL}/demandes/${id}/paiement`, { etatPaiement });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'état de paiement:", error);
    throw error;
  }
};

// Fonction pour mettre à jour les commentaires
export const majCom = async (chemin: string, commentaires: string) => {
  try {
    const response = await axios.put(`${BASE_URL}/majdemande/${chemin}/commentaires`, { commentaires });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des commentaires:', error);
    throw error;
  }
};

// Commentaires visa actuels d'un dossier, relus avant d'y ajouter une ligne : la route ci-dessous remplace tout le
// texte, et l'extension Chrome a pu en ajouter une depuis le chargement de la liste
export const recupereCommentairesVisa = async (demandeId: number): Promise<string> => {
  const response = await axios.get(`${BASE_URL}/demandes/${demandeId}`);
  return response.data?.commentairesvisa || '';
};

// Fonction pour mettre à jour les commentaires visa
export const majComVisa = async (chemin: string, commentairesVisa: string) => {
  try {
    const response = await axios.put(`${BASE_URL}/majdemande/${chemin}/commentairesvisa`, { commentairesVisa });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des commentaires visa:', error);
    throw error;
  }
};


export const majDemande = async (chemin: string, data: { datevalidite: string, dateexpiration: string, infosvisa: string }) => {
  try {
    const response = await axios.put(`${BASE_URL}/majdemande/${chemin}`, data);
    return response.data; // Retourne la réponse du serveur après la mise à jour
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la demande:', error);
    throw error; // Lance l'erreur pour une meilleure gestion
  }
};


export async function envoyerMailVisaTraite(chemin: string, data: {
  pays: string;
  datevalidite: string;
  dateexpiration: string;
  infosvisa: string;
  subjectMail: string;
  mailContent: string;
  sendEmail?: string;
}) {
  // On envoie toutes les infos au backend
  // Note : data.pays, data.subjectMail, data.mailContent etc.
  await axios.post(`${BASE_URL}/voyageurs/${chemin}/visaTraite`, data);
}




// Services.ts
export const uploadVisa = async (dossier: any, formData: any) => {
  // Remplacez l'URL par celle de votre API
  const url = `${BASE_URL}/voyageurs/${dossier}/uploadVisa`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData, // Envoie le FormData contenant le fichier
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload du visa');
  }

  return await response.json(); // Renvoie la réponse du serveur
};

export const uploadEcard = async (dossier: any, formData: any) => {
  const url = `${BASE_URL}/voyageurs/${dossier}/uploadEcard`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData, // Envoie le FormData contenant le fichier e-card
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload de l\'e-card');
  }

  return await response.json(); // Renvoie la réponse du serveur
};

// Dans vos services
export const recupereUtilisateurs = async () => {
  try {
    const response = await fetch(`${BASE_URL}/utilisateurs`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des utilisateurs:', error);
    throw error; // Renvoyer l'erreur pour une meilleure gestion
  }
};

export const supprimerUtilisateur = async (idUtilisateur: number): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/utilisateurs/${idUtilisateur}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la suppression de l\'utilisateur');
    }
  } catch (error) {
    console.error('Erreur dans supprimerUtilisateur:', error);
    throw error;
  }
};



export const recupererPasseport = async (cheminVoyageur: string): Promise<string> => {
  try {
    // Assurez-vous que l'URL et le paramètre sont correctement configurés pour votre backend
    const response = await axios.get(`${BASE_URL}/voyageurs/passeport`, { params: { chemin: cheminVoyageur } });
    return response.data.urlPasseport; // Assurez-vous que le backend renvoie un objet avec la clé 'urlPasseport'
  } catch (error) {
    console.error("Erreur lors de la récupération de l'URL du passeport", error);
    throw error;
  }
};

// Récupère l'URL de l'e-card d'une demande si elle a été uploadée (renvoie null si absente)
export const recupererEcard = async (chemin: string): Promise<string | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/demandes/${chemin}/ecard`);
    return response.data.urlEcard || null;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    console.error("Erreur lors de la récupération de l'URL de l'e-card", error);
    return null;
  }
};

// Pièces jointes du dossier d'une demande (« Informations du formulaire ») : [{ nom, url, supprimable }]
export const listerPiecesJointes = async (chemin: string): Promise<PieceJointe[]> =>
  (await axios.get<PieceJointe[]>(`${BASE_URL}/piecesJointes/${encodeURIComponent(chemin)}/fichiers`)).data;

// Suppression d'un document déposé par erreur (rangé dans supprimes/ sur le serveur) ; l'erreur porte le message du backend
export const supprimerPieceJointe = async (chemin: string, nom: string): Promise<void> => {
  const response = await fetch(
    `${BASE_URL}/piecesJointes/${encodeURIComponent(chemin)}/fichiers/${encodeURIComponent(nom)}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error((data && data.message) || 'Erreur lors de la suppression du document.');
  }
};

export const recupererContenuFormulaire = async (chemin: string) => {
  try {
    const reponse = await axios.get(`${BASE_URL}/formulaire/${chemin}`);
    return reponse.data; // Supposons que le backend renvoie directement le contenu JSON
  } catch (erreur) {
    console.error("Erreur lors de la récupération du contenu du formulaire", erreur);
    throw erreur;
  }
};

// Fonction pour récupérer les informations du client en fonction de l'email
export const recupereInfosClient = async (email: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/utilisateur/${email}`);
    return response.data; // Supposons que le serveur renvoie les infos du client dans la réponse
  } catch (error) {
    console.error('Erreur lors de la récupération des informations du client:', error);
    throw error;
  }
};

// Fonction pour récupérer la liste des demandes
export const recupereListeDemandes = async (includeArchived: boolean = false): Promise<Demandes[]> => {
    try {
      const response = await axios.get<Demandes[]>(`${BASE_URL}/demandes`, {
        params: { includeArchived }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      throw error;
    }
  };

// Fonction pour archiver une demande
export const archiverDemande = async (id: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/demandes/${id}/archiver`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'archivage de la demande:', error);
    throw error;
  }
};

// Fonction pour désarchiver une demande
export const desarchiverDemande = async (id: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/demandes/${id}/desarchiver`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du désarchivage de la demande:', error);
    throw error;
  }
};

  export const recupererInfoVoyageur = async (idVoyageur: number): Promise<Voyageurs> => {
    try {
      const response = await axios.get(`${BASE_URL}/voyageurs/${idVoyageur}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  export const modifierInfoVoyageur = async (voyageur: Voyageurs): Promise<void> => {
    try {
      await axios.put(`${BASE_URL}/voyageurs/${voyageur.id}`, voyageur);
    } catch (error) {
      throw error;
    }
  };

  // Fonction pour récupérer les bons de commande
export const recupereBDC = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/bdc`);
    return response.data; // Supposons que le serveur renvoie directement la liste des BDC
  } catch (error) {
    throw error;
  }
};

export const recupererDemandesBDC = async (idBonDeCommande:any) => {
  // Remplacez cette URL par l'URL de votre API
  const response = await fetch(`${BASE_URL}/bdc/${idBonDeCommande}/demandes`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des demandes pour le bon de commande');
  }
  return await response.json();
};

// À ajouter dans votre fichier Services.ts

// Upload d'un nouveau passeport 
export const uploadPasseport = async (dossier: string, formData: FormData): Promise<any> => {
  const response = await fetch(`${BASE_URL}/voyageurs/${dossier}/uploadPasseport`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload du passeport');
  }

  return response.json();
};

// Upload d'une nouvelle photo de voyageur
export const uploadPhotoVoyageur = async (idVoyageur: number, formData: FormData): Promise<any> => {
  const response = await fetch(`${BASE_URL}/voyageurs/${idVoyageur}/uploadPhoto`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload de la photo');
  }

  return response.json();
};

// Fonction pour supprimer les commentaires visa et passer l'état à "à traiter"
export const supprimerComVisaEtPasserATraiter = async (chemin: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/majdemande/${chemin}/commentairesvisa-et-traiter`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression des commentaires visa et passage à traiter:', error);
    throw error;
  }
};

// Upload d'un document générique dans le dossier de la demande
export const uploadDocumentDemande = async (chemin: string, formData: FormData): Promise<any> => {
  const response = await fetch(`${BASE_URL}/demandes/${chemin}/uploadDocument`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload du document');
  }

  return response.json();
};



